// Badge service for interacting with the achievements/badges API
import { API_BASE } from '../config/api';
import AuthService from './authService';

class BadgeService {
  static baseURL = `${API_BASE}/api`;

  // Get all badge definitions
  static async getBadgeDefinitions() {
    try {
      const response = await fetch(`${this.baseURL}/badges`);
      const data = await response.json();

      if (data.status === 'success') {
        return { success: true, badges: data.data };
      } else {
        return { success: false, error: data.message };
      }
    } catch (error) {
      console.error('Error fetching badges:', error);
      return { success: false, error: error.message };
    }
  }

  // Get badges by category
  static async getBadgesByCategory(category) {
    try {
      const response = await fetch(`${this.baseURL}/badges/category/${category}`);
      const data = await response.json();

      if (data.status === 'success') {
        return { success: true, badges: data.data };
      } else {
        return { success: false, error: data.message };
      }
    } catch (error) {
      console.error('Error fetching badges by category:', error);
      return { success: false, error: error.message };
    }
  }

  // Get regional badges for a country
  static async getRegionalBadges(countryCode, adminLevel1 = null) {
    try {
      let url = `${this.baseURL}/badges/region/${countryCode}`;
      if (adminLevel1) {
        url += `?adminLevel1=${encodeURIComponent(adminLevel1)}`;
      }

      const response = await fetch(url);
      const data = await response.json();

      if (data.status === 'success') {
        return { success: true, badges: data.data };
      } else {
        return { success: false, error: data.message };
      }
    } catch (error) {
      console.error('Error fetching regional badges:', error);
      return { success: false, error: error.message };
    }
  }

  // Get user's badges and progress
  static async getUserBadges(userId) {
    try {
      const response = await fetch(`${this.baseURL}/badges/user/${userId}`, {
        headers: AuthService.getJsonHeaders()
      });
      const data = await response.json();

      if (data.status === 'success') {
        return {
          success: true,
          badges: data.data.badges,
          unlocked: data.data.unlocked,
          unlockedCount: data.data.unlockedCount,
          totalBadges: data.data.totalBadges
        };
      } else {
        return { success: false, error: data.message };
      }
    } catch (error) {
      console.error('Error fetching user badges:', error);
      return { success: false, error: error.message };
    }
  }

  // Check and award badges for a user
  static async checkAndAwardBadges(userId) {
    try {
      const response = await fetch(`${this.baseURL}/badges/check/${userId}`, {
        method: 'POST',
        headers: AuthService.getJsonHeaders()
      });
      const data = await response.json();

      if (data.status === 'success') {
        return {
          success: true,
          awarded: data.data.awarded,
          updated: data.data.updated
        };
      } else {
        return { success: false, error: data.message };
      }
    } catch (error) {
      console.error('Error checking badges:', error);
      return { success: false, error: error.message };
    }
  }

  // Get unnotified badges (for showing unlock notifications)
  static async getUnnotifiedBadges(userId) {
    try {
      const response = await fetch(`${this.baseURL}/badges/user/${userId}/unnotified`, {
        headers: AuthService.getJsonHeaders()
      });
      const data = await response.json();

      if (data.status === 'success') {
        return { success: true, badges: data.data };
      } else {
        return { success: false, error: data.message };
      }
    } catch (error) {
      console.error('Error fetching unnotified badges:', error);
      return { success: false, error: error.message };
    }
  }

  // Mark a badge as notified
  static async markBadgeNotified(badgeId) {
    try {
      const response = await fetch(`${this.baseURL}/badges/${badgeId}/notified`, {
        method: 'POST',
        headers: AuthService.getJsonHeaders()
      });
      const data = await response.json();

      if (data.status === 'success') {
        return { success: true };
      } else {
        return { success: false, error: data.message };
      }
    } catch (error) {
      console.error('Error marking badge notified:', error);
      return { success: false, error: error.message };
    }
  }

  // Get global leaderboard
  static async getGlobalLeaderboard(limit = 10) {
    try {
      const response = await fetch(`${this.baseURL}/badges/leaderboard?limit=${limit}`);
      const data = await response.json();

      if (data.status === 'success') {
        return { success: true, leaderboard: data.data };
      } else {
        return { success: false, error: data.message };
      }
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
      return { success: false, error: error.message };
    }
  }

  // Get regional leaderboard
  static async getRegionalLeaderboard(countryCode, adminLevel1 = null, limit = 10) {
    try {
      let url = `${this.baseURL}/badges/leaderboard/${countryCode}?limit=${limit}`;
      if (adminLevel1) {
        url += `&adminLevel1=${encodeURIComponent(adminLevel1)}`;
      }

      const response = await fetch(url);
      const data = await response.json();

      if (data.status === 'success') {
        return { success: true, leaderboard: data.data };
      } else {
        return { success: false, error: data.message };
      }
    } catch (error) {
      console.error('Error fetching regional leaderboard:', error);
      return { success: false, error: error.message };
    }
  }

  // Get badge statistics
  static async getBadgeStats() {
    try {
      const response = await fetch(`${this.baseURL}/badges/stats`, {
        headers: AuthService.getJsonHeaders()
      });
      const data = await response.json();

      if (data.status === 'success') {
        return { success: true, stats: data.data };
      } else {
        return { success: false, error: data.message };
      }
    } catch (error) {
      console.error('Error fetching badge stats:', error);
      return { success: false, error: error.message };
    }
  }

  // Helper: Get unlocked badges only
  static async getUnlockedBadges(userId) {
    const result = await this.getUserBadges(userId);
    if (result.success) {
      return {
        success: true,
        badges: result.badges.filter(b => b.unlocked)
      };
    }
    return result;
  }

  // Helper: Get in-progress badges (not unlocked, but have some progress)
  static async getInProgressBadges(userId) {
    const result = await this.getUserBadges(userId);
    if (result.success) {
      return {
        success: true,
        badges: result.badges.filter(b => !b.unlocked && b.current_count > 0)
      };
    }
    return result;
  }

  // Helper: Group badges by category
  static groupBadgesByCategory(badges) {
    const grouped = {};
    badges.forEach(badge => {
      const category = badge.category || 'other';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(badge);
    });
    return grouped;
  }

  // Helper: Group badges by region
  static groupBadgesByRegion(badges) {
    const grouped = {
      global: [],
      countries: {},
      regions: {}
    };

    badges.forEach(badge => {
      if (!badge.country_code) {
        grouped.global.push(badge);
      } else if (!badge.admin_level_1) {
        if (!grouped.countries[badge.country_code]) {
          grouped.countries[badge.country_code] = [];
        }
        grouped.countries[badge.country_code].push(badge);
      } else {
        const key = `${badge.country_code}:${badge.admin_level_1}`;
        if (!grouped.regions[key]) {
          grouped.regions[key] = [];
        }
        grouped.regions[key].push(badge);
      }
    });

    return grouped;
  }
}

export default BadgeService;
